const normalizeIngredientName = (value) => value?.trim?.() ?? '';

export const mapApiIngredientToMealIngredient = (ingredient = {}) => ({
  id: ingredient?.id,
  english: normalizeIngredientName(
    ingredient?.nameEn ?? ingredient?.english ?? ingredient?.contentsEn
  ),
  nor: normalizeIngredientName(
    ingredient?.nameNo ?? ingredient?.nor ?? ingredient?.contentsNo
  ),
});

export const areMealIngredientsEqual = (
  firstIngredient = {},
  secondIngredient = {}
) => {
  if (firstIngredient?.id && secondIngredient?.id) {
    return firstIngredient.id === secondIngredient.id;
  }

  return (
    normalizeIngredientName(firstIngredient?.english).toLowerCase() ===
      normalizeIngredientName(secondIngredient?.english).toLowerCase() &&
    normalizeIngredientName(firstIngredient?.nor).toLowerCase() ===
      normalizeIngredientName(secondIngredient?.nor).toLowerCase()
  );
};

export const dedupeMealIngredients = (ingredients = []) => {
  return ingredients.filter(
    (ingredient, index) =>
      ingredients.findIndex((candidate) =>
        areMealIngredientsEqual(candidate, ingredient)
      ) === index
  );
};

export const sortMealIngredients = (ingredients = []) => {
  return [...ingredients].sort((firstIngredient, secondIngredient) => {
    const englishComparison = (firstIngredient?.english ?? '').localeCompare(
      secondIngredient?.english ?? ''
    );

    if (englishComparison !== 0) {
      return englishComparison;
    }

    return (firstIngredient?.nor ?? '').localeCompare(secondIngredient?.nor ?? '');
  });
};

export const formatMealIngredientLabel = (ingredient = {}) => {
  return `${ingredient?.english ?? ''}/${ingredient?.nor ?? ''}`;
};

export const findMatchingApiIngredient = (apiIngredients = [], payload = {}) => {
  const normalizedEnglish = normalizeIngredientName(payload?.nameEn).toLowerCase();
  const normalizedNorwegian = normalizeIngredientName(payload?.nameNo).toLowerCase();

  return apiIngredients.find(
    (ingredient) =>
      normalizeIngredientName(ingredient?.nameEn).toLowerCase() === normalizedEnglish &&
      normalizeIngredientName(ingredient?.nameNo).toLowerCase() ===
        normalizedNorwegian
  );
};

export const getMealIngredientIds = (
  ingredients = [],
  availableIngredients = []
) => {
  return dedupeMealIngredients(ingredients)
    .map((ingredient) => {
      if (ingredient?.id) {
        return ingredient.id;
      }

      return findMatchingApiIngredient(availableIngredients, {
        nameEn: ingredient?.english,
        nameNo: ingredient?.nor,
      })?.id;
    })
    .filter(Boolean);
};

const hasMealIngredientLabel = (ingredient = {}) => {
  return Boolean(
    normalizeIngredientName(ingredient?.english) ||
      normalizeIngredientName(ingredient?.nor)
  );
};

export const getMealIngredients = (meal = {}) => {
  const ingredients = meal?.Ingredients ?? meal?.ingredients ?? [];

  if (!Array.isArray(ingredients) || ingredients.length === 0) {
    return [];
  }

  return dedupeMealIngredients(
    ingredients
      .filter(
        (ingredient) => ingredient && typeof ingredient === 'object' && !Array.isArray(ingredient)
      )
      .map((ingredient) => mapApiIngredientToMealIngredient(ingredient))
      .filter((ingredient) => hasMealIngredientLabel(ingredient))
  );
};
